import * as React from "react";
import { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import "../../../styles/rosterUpload.css";
import { api } from "../../../../../utils/axios";

interface RosterUploadProps {
    clientId: string;
    clientName: string;
}

const REQUIRED_FIELDS = [
    "customer_first_name",
    "phone",
    "email",
    "customer_plan_name",
];

const OPTIONAL_FIELDS = ["customer_middle_name", "customer_last_name"];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

const RosterUpload: React.FC<RosterUploadProps> = ({ clientId, clientName }) => {
    const [file, setFile] = useState<File | null>(null);
    const [sheets, setSheets] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState<string>("");
    const [columns, setColumns] = useState<string[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [previewRows, setPreviewRows] = useState<any[]>([]);
    const [rawData, setRawData] = useState<any[]>([]);
    const [existingUserCount, setExistingUserCount] = useState<number | null>(null);
    const [missingPlans, setMissingPlans] = useState<string[]>([]);
    const [updateResult, setUpdateResult] = useState<string | null>(null);
    const [hasValidated, setHasValidated] = useState<boolean>(false);
    const [invalidRecords, setInvalidRecords] = useState<any[]>([]);
    const [apiLoading, setApiLoading] = useState<boolean>(false);


    const allFieldsMapped = REQUIRED_FIELDS.every((field) => mapping[field]);

    const parseSheet = async (file: File, sheetName: string) => {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[sheetName];
        return XLSX.utils.sheet_to_json(sheet, { defval: "" });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploaded = event.target.files?.[0];
        if (!uploaded) return;

        setFile(uploaded);
        setLoading(true);

        try {
            const data = await uploaded.arrayBuffer();
            const workbook = XLSX.read(data, { type: "array" });
            const sheetNames = workbook.SheetNames;
            setSheets(sheetNames);
            setSelectedSheet(sheetNames[0]);

            const sheet = workbook.Sheets[sheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][];
            const headers = rows[0] as string[];
            setColumns(headers);

            const parsed = await parseSheet(uploaded, sheetNames[0]);
            setRawData(parsed);
        } catch (err) {
            console.error("Error parsing file", err);
            setSheets([]);
            setColumns([]);
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSheetChange = async (sheetName: string) => {
        if (!file) return;
        setSelectedSheet(sheetName);
        setLoading(true);

        try {
            const parsed = await parseSheet(file, sheetName);
            const headers = Object.keys(parsed[0] || {});
            setColumns(headers);
            setRawData(parsed);
        } catch (err) {
            console.error("Error switching sheet", err);
            setColumns([]);
            setRawData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMappingChange = (field: string, value: string) => {
        const updated = { ...mapping, [field]: value };
        setMapping(updated);
    };

    useEffect(() => {
        if (!rawData.length || !allFieldsMapped) {
            setPreviewRows([]);
            return;
        }

        const mapped = rawData.slice(0, 5).map((row) => ({
            customer_name: [
                row[mapping["customer_first_name"]],
                mapping["customer_middle_name"] ? row[mapping["customer_middle_name"]] : "",
                mapping["customer_last_name"] ? row[mapping["customer_last_name"]] : "",
            ]
                .filter(Boolean)
                .join(" ")
                .trim(),
            phone: row[mapping["phone"]],
            email: row[mapping["email"]],
            customer_plan_name: row[mapping["customer_plan_name"]],
        }));

        setPreviewRows(mapped);
    }, [mapping, rawData]);

    const buildPayload = () => ({
        client_id: clientId,
        client_name: clientName,
        data: rawData.map((row) => ({
            customer_name: [
                row[mapping["customer_first_name"]],
                mapping["customer_middle_name"] ? row[mapping["customer_middle_name"]] : "",
                mapping["customer_last_name"] ? row[mapping["customer_last_name"]] : "",
            ]
                .filter(Boolean)
                .join(" ")
                .trim(),
            phone: row[mapping["phone"]],
            email: row[mapping["email"]],
            customer_plan_name: row[mapping["customer_plan_name"]],
        })),
    });

    const handleValidate = async () => {
        setApiLoading(true);
        try {
            const payload = buildPayload();
            const res = await api.post("/admin/rosters/validate-upload", payload);
            const { existing_user_count, missing_customer_plans, invalid_records } = res.data;

            setExistingUserCount(existing_user_count);
            setMissingPlans(missing_customer_plans || []);
            setInvalidRecords(invalid_records || []);
            setHasValidated(true);
        } catch (err) {
            console.error("Validation failed", err);
            alert("Validation failed");
        } finally {
            setApiLoading(false);
        }
    };


    const handleUpdate = async (): Promise<void> => {
        setApiLoading(true);
        try {
            const payload = buildPayload();
            const response = await api.post("/admin/rosters/append-to-eligibility", payload);
            setUpdateResult(response.data || "Update successful");
        } catch (error) {
            console.error("Update failed:", error);
            setUpdateResult("Update failed");
        } finally {
            setApiLoading(false);
        }
    };

    const handleOverride = async (): Promise<void> => {
        setApiLoading(true);
        try {
            const payload = buildPayload();
            const response = await api.post("/admin/rosters/overwrite-eligibility", payload);
            setUpdateResult(response.data || "Override successful");
        } catch (error) {
            console.error("Override failed:", error);
            setUpdateResult("Override failed");
        } finally {
            setApiLoading(false);
        }
    };

    const handleAppendManual = async (): Promise<void> => {
        try {
            const payload = buildPayload();
            const response = await api.post("/admin/rosters/append-from-manual", payload);
            setUpdateResult(response.data || "Manual append successful");
        } catch (error) {
            console.error("Manual append failed:", error);
            setUpdateResult("Manual append failed");
        }
    };


    return (
        <Box className="roster-upload-container">
            <Typography variant="h5" gutterBottom>
                Upload Roster File
            </Typography>

            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                Upload CSV/XLS/XLSX
                <input hidden type="file" accept=".csv, .xls, .xlsx" onChange={handleFileChange} />
            </Button>

            {loading && <CircularProgress sx={{ mt: 2 }} />}

            {file && !loading && (
                <>
                    <Typography sx={{ mt: 2 }}>Selected File: {file.name}</Typography>

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Select Sheet</InputLabel>
                        <Select
                            value={selectedSheet}
                            onChange={(e) => handleSheetChange(e.target.value)}
                            label="Select Sheet"
                        >
                            {sheets.map((sheet) => (
                                <MenuItem key={sheet} value={sheet}>
                                    {sheet}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6">Map Columns</Typography>
                        {ALL_FIELDS.map((field) => (
                            <FormControl fullWidth sx={{ mt: 2 }} key={field}>
                                <InputLabel>{field.replace(/_/g, " ")}</InputLabel>
                                <Select
                                    value={mapping[field] || ""}
                                    label={field}
                                    onChange={(e) => handleMappingChange(field, e.target.value)}
                                >
                                    {columns.map((col) => (
                                        <MenuItem key={col} value={col}>
                                            {col}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}
                    </Box>

                    {previewRows.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Preview (First 5 Rows)
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Phone</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Customer Plan</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewRows.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{row.customer_name}</TableCell>
                                                <TableCell>{row.phone}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.customer_plan_name}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    <Button
                        sx={{ mt: 4 }}
                        variant="contained"
                        color="primary"
                        disabled={!allFieldsMapped}
                        onClick={handleValidate}
                    >
                        Validate
                    </Button>

                    {existingUserCount !== null && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6">Validation Summary</Typography>
                            <Typography sx={{ mt: 1 }}>
                                ✅ Existing users in system: <strong>{existingUserCount}</strong>
                            </Typography>
                            <Typography sx={{ mt: 1 }} display="flex" alignItems="center">
                                {missingPlans.length === 0 ? (
                                    <span style={{ color: "green" }}>✅ All customer plans are valid</span>
                                ) : (
                                    <>
                                        ❌ Missing customer plans:
                                        <span style={{ marginLeft: 6, color: "red" }}>
                                            {missingPlans.join(", ")}
                                        </span>
                                    </>
                                )}
                            </Typography>
                            <Typography>
                                {invalidRecords.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="h6" color="error">
                                            ❌ Invalid Records ({invalidRecords.length})
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Email</TableCell>
                                                        <TableCell>Phone</TableCell>
                                                        <TableCell>Plan</TableCell>
                                                        <TableCell>Reason</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {invalidRecords.map((row, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{row.customer_name}</TableCell>
                                                            <TableCell>{row.email}</TableCell>
                                                            <TableCell>{row.phone}</TableCell>
                                                            <TableCell>{row.customer_plan_name}</TableCell>
                                                            <TableCell sx={{ color: "red" }}>{row.reason}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </Typography>

                            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={!hasValidated}
                                    onClick={handleUpdate}
                                >
                                    Update Rosters
                                </Button>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    disabled={!hasValidated}
                                    onClick={handleOverride}
                                >
                                    Override Rosters
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleAppendManual}
                                >
                                    Append from Manual
                                </Button>
                            </Box>

                            {apiLoading && (
                                <Box sx={{ mt: 2 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Processing...
                                    </Typography>
                                </Box>
                            )}

                            {updateResult && (
                                <Typography sx={{ mt: 2, color: "blue", whiteSpace: "pre-line" }}>
                                    📬{" "}
                                    {typeof updateResult === "string"
                                        ? updateResult
                                        : `Status: ${updateResult.status}
                                           Message: ${updateResult.message}`}
                                </Typography>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default RosterUpload;
